import { ShareBestTimesLinkModal } from "#webapp/src/components/scheduling-link";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { Button } from "@clockwise/design-system";
import {
  useGatewayMutation,
  useGatewayQuery,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { SchedulingLinkCardSkeleton } from "@clockwise/web-commons/src/ui/scheduling-link-card/SchedulingLinkCardSkeleton";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { useWindowFocus } from "@clockwise/web-commons/src/util/react.util";
import { Launch } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { useRouter } from "found";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IReduxState } from "../../state/reducers/root.reducer";
import { ChromeDialog } from "../chrome-dialog";
import {
  DEFAULT_LINK,
  LinksSection,
  SidebarLink,
  findInitialDefaultLink,
  isDefaultLink,
} from "./LinksSection";
import { CompactLinksQueryDocument } from "./__generated__/CompactLinksQuery.v2.generated";
import { FindOrCreateLinkNameDocument } from "./__generated__/FindOrCreateLinkName.v2.generated";
import { getLinksByType } from "./utils";

// popupScript listens for "setHeight" messages and resizes the Clockwise LINK.html iframe accordingly.
export const MAX_HEIGHT = 595;
export const setPopupHeight = (height: number) =>
  parent.postMessage(["setHeight", Math.min(height, MAX_HEIGHT)], "*");

interface Props {
  surface: "sidebar" | "mini";
}

export const SchedulingLinksCompact = ({ surface }: Props) => {
  const trackEvent = useTracking();
  const router = useRouter()?.router;
  const openUrl = router?.push ?? window.open;
  const openShareLinkModal = useSelector(
    (state: IReduxState) => state.webExtension.openShareLinkModal,
  );

  const [modalLink, setModalLink] = useState<SidebarLink | null>(null);

  const { data, loading, error, refetch } = useGatewayQuery(CompactLinksQueryDocument);

  const focused = useWindowFocus();
  useEffect(() => {
    if (focused) {
      void refetch();
    }
  }, [focused, refetch]);

  const currentUserLinkName = data?.currentUser?.linkName;
  const schedulingLinks = data?.currentUser?.schedulingLinks ?? [];

  // No linkName is present means that the user has never made their owner link. Include the
  // default link in the list of links to display, and still display schedulingLinks as the list
  // may be populated with group links that the user is a member of.
  const linksOrDefault = !currentUserLinkName
    ? [...schedulingLinks, DEFAULT_LINK]
    : schedulingLinks;

  const { normalLinks, groupLinks, singleUseLinks, inactiveLinks } = getLinksByType(linksOrDefault);

  const [findOrCreateDefaultLinkName] = useGatewayMutation(FindOrCreateLinkNameDocument);

  // Create the user's actual "Quick Meeting" link, and then return it for use by the various "on"'s below.
  const createRealQuickMeeting = useCallback(async () => {
    const result = await findOrCreateDefaultLinkName();
    const linkName = result.data?.createDefaultLinkName?.linkName;
    const initialLink = findInitialDefaultLink(result.data?.createDefaultLinkName?.schedulingLinks);
    return [linkName, initialLink] as [string | null | undefined, typeof initialLink];
  }, [findOrCreateDefaultLinkName]);

  const onOpenUrl = useCallback(
    (url: string) => {
      setPopupHeight(MAX_HEIGHT);
      openUrl(url);
    },
    [openUrl],
  );

  const onChangeModalLink = useCallback((link: SidebarLink) => {
    setPopupHeight(MAX_HEIGHT);
    setModalLink(link);
  }, []);

  const onOpenShareModal = useCallback(
    async (link: SidebarLink) => {
      let toTrack = link;
      setPopupHeight(MAX_HEIGHT);
      if (isDefaultLink(link)) {
        const [, realQuickMeeting] = await createRealQuickMeeting();

        if (realQuickMeeting) {
          setModalLink(realQuickMeeting);
          toTrack = realQuickMeeting;
        }
      } else {
        setModalLink(link);
      }
      trackEvent(TrackingEvents.LINKS.LINK.COPY_TIMES, {
        meetingSettingsId: toTrack.schedulingLink.id,
        surface,
      });
    },
    [createRealQuickMeeting, surface, trackEvent],
  );

  // Marketing feature to open the share modal upon a certain url para set. Search for "openShareLinkModal".
  useEffect(() => {
    if (openShareLinkModal) {
      setTimeout(() => {
        if (linksOrDefault.length) {
          void onOpenShareModal(linksOrDefault[0]);
        }
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openShareLinkModal]);

  const onCreateLink = (singleUse = false) => {
    trackEvent(TrackingEvents.LINKS.LINK.CREATE_LINK, {
      surface,
      singleUse,
    });
    setPopupHeight(MAX_HEIGHT);
    openUrl(paths.createSchedulingLink + (singleUse ? "?single_use=true" : ""));
  };

  const onManageLinks = () => {
    window.open(paths.schedulingLinks, "links");
    trackEvent(TrackingEvents.LINKS.LINK.MANAGE_LINKS, {
      surface,
    });
  };

  if (loading) {
    return (
      <div className="cw-space-y-3">
        <div className="cw-flex cw-gap-3">
          <Skeleton variant="rect" width={60} height={32} className="cw-rounded-md" />
          <Skeleton variant="rect" width={122} height={32} className="cw-rounded-md" />
        </div>
        <div className="cw-space-y-3">
          <SchedulingLinkCardSkeleton />
          <SchedulingLinkCardSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cw-space-y-3">
        <div className="cw-flex cw-gap-3">
          <Button
            size="small"
            onClick={() => onCreateLink(false)}
            cw-id="create-links-button"
            sentiment="positive"
          >
            Create
          </Button>
          <Button
            size="small"
            onClick={() => onCreateLink(true)}
            cw-id="create-links-button"
            sentiment="positive"
          >
            Create single-use
          </Button>
          {surface === "sidebar" && (
            <Button
              size="small"
              onClick={onManageLinks}
              cw-id="manage-links-button"
              sentiment="neutral"
              variant="outlined"
              startIcon={Launch}
              title="Manage links"
            />
          )}
        </div>
        <div>
          <div className="cw-bg-warning cw-text-warning cw-border cw-border-solid cw-border-warning cw-rounded-md cw-p-3">
            <div className="cw-space-y-3">
              <div>There was an issue loading your scheduling links.</div>
              <Button size="small" onClick={() => void refetch()} variant="outlined">
                Try again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="cw-space-y-3">
        <div className="cw-flex cw-gap-3">
          <Button
            size="small"
            onClick={() => onCreateLink(false)}
            cw-id="create-links-button"
            sentiment="positive"
          >
            Create
          </Button>
          <Button
            size="small"
            onClick={() => onCreateLink(true)}
            cw-id="create-links-button"
            sentiment="positive"
          >
            Create single-use
          </Button>
          {surface === "sidebar" && (
            <Button
              size="small"
              onClick={onManageLinks}
              cw-id="manage-links-button"
              sentiment="neutral"
              variant="outlined"
              startIcon={Launch}
              title="Manage links"
            />
          )}
        </div>
        {!!normalLinks.length && (
          <LinksSection
            links={normalLinks}
            onOpenUrl={onOpenUrl}
            onChangeModalLink={onChangeModalLink}
            surface={surface}
          />
        )}
        {!!groupLinks.length && (
          <LinksSection
            title="Group Links"
            links={groupLinks}
            onOpenUrl={onOpenUrl}
            onChangeModalLink={onChangeModalLink}
            surface={surface}
          />
        )}
        {!!singleUseLinks.length && (
          <LinksSection
            title="Single-use"
            links={singleUseLinks}
            onOpenUrl={onOpenUrl}
            onChangeModalLink={onChangeModalLink}
            surface={surface}
          />
        )}
        {!!inactiveLinks.length && (
          <LinksSection
            title="Inactive"
            links={inactiveLinks}
            onOpenUrl={onOpenUrl}
            onChangeModalLink={onChangeModalLink}
            surface={surface}
          />
        )}
      </div>
      {modalLink?.linkName && (
        <ChromeDialog open>
          <ShareBestTimesLinkModal
            url={modalLink.url}
            linkName={modalLink.linkName}
            name={modalLink.schedulingLink.name}
            slug={modalLink.schedulingLink.slug}
            durations={modalLink.schedulingLink.durations}
            defaultDuration={modalLink.schedulingLink.defaultDuration}
            windowSizeDays={
              modalLink.schedulingLink.availabilityRange?.__typename === "RollingWindow"
                ? modalLink.schedulingLink.availabilityRange.windowSize
                : null
            }
            multipleUser={modalLink.schedulingLink.linkMembers.length > 1}
            onClose={() => setModalLink(null)}
            fullscreen={surface === "mini"}
          />
        </ChromeDialog>
      )}
    </>
  );
};
